import type { Project } from '@awork/features/project/models/project.model'
import { ProjectMember } from '@awork/features/project/models/project-member.model'
import { getMonthYearString, getWeekYearString } from '@awork/_shared/functions/date-operations'
import { GroupByOptions, ProjectSortByOptions } from '@awork/_shared/models/group-by-options.model'
import type { SortingDirection } from '@awork/_shared/models/sort-by-option.model'
import { ProjectStatus } from '../models/project-status.model'
import type { Translations } from '@awork/i18n/translations'
import type { ISOLanguage } from '@awork/_shared/models/account.model'
import type { WorkflowQuery } from '@awork/features/workflow/state/workflow.query'

/**
 * Gets project sorting according to the group
 * @param {Project} projectA
 * @param {Project} projectB
 * @param {WorkflowQuery} workflowQuery
 * @param {GroupByOptions} projectGrouping
 * @returns {number}
 */
// eslint-disable-next-line complexity
export function getSortByGroup(
  projectA: Project,
  projectB: Project,
  workflowQuery: WorkflowQuery,
  projectGrouping?: GroupByOptions
): number {
  let sortResult: number

  switch (projectGrouping) {
    case GroupByOptions.client:
      if (!projectA.company?.name) {
        sortResult = 1
        break
      }

      if (!projectB.company?.name) {
        sortResult = -1
        break
      }

      sortResult = projectA.company?.name.localeCompare(projectB.company?.name)
      break
    case GroupByOptions.projectStatus: {
      const typeOrderA = projectA.projectStatus?.typeOrder ?? Number.MAX_SAFE_INTEGER
      const typeOrderB = projectB.projectStatus?.typeOrder ?? Number.MAX_SAFE_INTEGER

      if (typeOrderA === typeOrderB) {
        sortResult = projectA.projectStatus?.name.localeCompare(projectB.projectStatus?.name) || 0
      } else {
        sortResult = typeOrderA - typeOrderB
      }

      break
    }
    case GroupByOptions.projectStatusType: {
      const projectAStatusType = ProjectStatus.types.find(type => type.key === projectA.projectStatus?.type)
      const projectBStatusType = ProjectStatus.types.find(type => type.key === projectB.projectStatus?.type)

      if (projectAStatusType?.order === projectBStatusType?.order) {
        sortResult = projectA.projectStatus?.name.localeCompare(projectB.projectStatus?.name) || 0
      } else {
        sortResult = projectAStatusType?.order - projectBStatusType?.order || 0
      }

      break
    }
    case GroupByOptions.projectType:
      sortResult = projectA.projectType?.name.localeCompare(projectB.projectType?.name)
      break
    case GroupByOptions.responsibleUser: {
      const projectAResponsibleUserName = getResponsibleUserName(projectA)
      const projectBResponsibleUserName = getResponsibleUserName(projectB)

      if (!projectAResponsibleUserName) {
        sortResult = 1
        break
      }

      if (!projectBResponsibleUserName) {
        sortResult = -1
        break
      }

      sortResult = projectAResponsibleUserName.localeCompare(projectBResponsibleUserName)
      break
    }
    case GroupByOptions.dueWeek:
    case GroupByOptions.dueMonth: {
      if (!projectA.dueDate) {
        sortResult = 1
        break
      }

      if (!projectB.dueDate) {
        sortResult = -1
        break
      }

      const stringFunction = projectGrouping === GroupByOptions.dueWeek ? getWeekYearString : getMonthYearString

      if (
        stringFunction(
          projectA.dueDate,
          q.translations as unknown as Translations['en'] & { currentLang: ISOLanguage }
        ) ===
        stringFunction(projectB.dueDate, q.translations as unknown as Translations['en'] & { currentLang: ISOLanguage })
      ) {
        sortResult = 0
        break
      }

      sortResult = new Date(projectA.dueDate) > new Date(projectB.dueDate) ? -1 : 1
      break
    }
    case GroupByOptions.workflow: {
      const workflowAName = projectA.workflowId ? workflowQuery.getWorkflowById(projectA.workflowId)?.name : null
      const workflowBName = projectB.workflowId ? workflowQuery.getWorkflowById(projectB.workflowId)?.name : null

      if (!workflowAName && !workflowBName) {
        sortResult = 0
        break
      }

      if (!workflowAName) {
        sortResult = 1
        break
      }

      if (!workflowBName) {
        sortResult = -1
        break
      }

      // Sort alphabetically by workflow name
      sortResult = workflowAName.localeCompare(workflowBName)
      break
    }
    default:
      sortResult = 0
      break
  }

  if (sortResult === 0) {
    return projectA?.name.localeCompare(projectB?.name)
  }

  return sortResult
}

/**
 * Gets project sorting according to the sorting configuration
 * @param {Project} projectA
 * @param {Project} projectB
 * @param {ProjectSortByOptions} projectSorting
 * @param {SortingDirection} [sortingDirection='asc']
 * @param {WorkflowQuery} workflowQuery - Required when sorting by workflow to resolve workflow names
 * @returns {number}
 */
// eslint-disable-next-line complexity
export function projectSortBy(
  projectA: Project,
  projectB: Project,
  projectSorting: ProjectSortByOptions,
  sortingDirection: SortingDirection = 'asc',
  workflowQuery?: WorkflowQuery
): number {
  let sortResult: number
  const direction = sortingDirection === 'asc' ? 1 : -1

  switch (projectSorting) {
    case ProjectSortByOptions.client:
      if (!projectA.company?.name) {
        sortResult = 1
        break
      }

      if (!projectB.company?.name) {
        sortResult = -1
        break
      }

      sortResult = projectA.company?.name.localeCompare(projectB.company?.name)
      break
    case ProjectSortByOptions.projectType:
      return sortByOptionalProjectText(projectA, projectB, project => project.projectType?.name, direction)
    case ProjectSortByOptions.responsibleUser:
      return sortByOptionalProjectText(projectA, projectB, getResponsibleUserName, direction)
    case ProjectSortByOptions.key:
      return sortByOptionalProjectText(projectA, projectB, project => project.projectKey, direction)
    case ProjectSortByOptions.status:
      if (!projectA.projectStatus) {
        sortResult = 1
        break
      }

      if (!projectB.projectStatus) {
        sortResult = -1
        break
      }

      if (projectA.projectStatus.typeOrder === projectB.projectStatus.typeOrder) {
        sortResult = projectA.projectStatus.name.localeCompare(projectB.projectStatus.name)
      } else {
        sortResult = projectA.projectStatus.typeOrder - projectB.projectStatus.typeOrder
      }

      break
    case ProjectSortByOptions.workflow: {
      const workflowAName = projectA.workflowId ? workflowQuery?.getWorkflowById(projectA.workflowId)?.name : null
      const workflowBName = projectB.workflowId ? workflowQuery?.getWorkflowById(projectB.workflowId)?.name : null

      if (!workflowAName && !workflowBName) {
        sortResult = 0
        break
      }

      if (!workflowAName) {
        return 1
      }

      if (!workflowBName) {
        return -1
      }

      sortResult = workflowAName.localeCompare(workflowBName)
      break
    }
    case ProjectSortByOptions.dueDate:
      sortResult = sortByDate(projectA?.dueDate, projectB?.dueDate)
      break
    case ProjectSortByOptions.startDate:
      sortResult = sortByDate(projectA?.startDate, projectB?.startDate)
      break
    case ProjectSortByOptions.createdOn:
      sortResult = sortByDate(projectA?.createdOn, projectB?.createdOn)
      break
    case ProjectSortByOptions.budget:
      sortResult = (projectA?.timeBudget || 0) - (projectB?.timeBudget || 0)
      break
    case ProjectSortByOptions.totalTasks: {
      const tasksCountA = projectA?.tasksCount || 0
      const tasksCountB = projectB?.tasksCount || 0

      sortResult = tasksCountA - tasksCountB

      if (tasksCountA === tasksCountB) {
        sortResult = (projectA?.tasksDoneCount || 0) - (projectB?.tasksDoneCount || 0)
      }

      break
    }
    case ProjectSortByOptions.taskProgress: {
      const progressA = projectA?.tasksCount > 0 ? (projectA.tasksDoneCount || 0) / projectA.tasksCount : 0
      const progressB = projectB?.tasksCount > 0 ? (projectB.tasksDoneCount || 0) / projectB.tasksCount : 0

      sortResult = progressA - progressB

      // If progress percentage is the same, sort by total number of tasks
      if (progressA === progressB) {
        sortResult = (projectA?.tasksCount || 0) - (projectB?.tasksCount || 0)
      }

      break
    }
    case ProjectSortByOptions.timeProgress: {
      const timeProgressA = projectA?.timeBudget > 0 ? (projectA.trackedDuration || 0) / projectA.timeBudget : 0
      const timeProgressB = projectB?.timeBudget > 0 ? (projectB.trackedDuration || 0) / projectB.timeBudget : 0

      sortResult = timeProgressA - timeProgressB

      if (timeProgressA === timeProgressB) {
        sortResult = (projectA?.trackedDuration || 0) - (projectB?.trackedDuration || 0)
      }

      break
    }
    case ProjectSortByOptions.name:
    default:
      sortResult = projectA?.name.localeCompare(projectB?.name)
      break
  }

  // If the sorting result is the same, sort by name
  if (sortResult === 0) {
    sortResult = projectA?.name.localeCompare(projectB?.name)
  }

  return sortResult * direction
}

function sortByDate(dateA: Date | string, dateB: Date | string): number {
  const dateObjectA = new Date(dateA)
  const dateObjectB = new Date(dateB)

  return (dateObjectA?.getTime() || 0) - (dateObjectB?.getTime() || 0)
}

/**
 * Gets the displayed name of a project's responsible user.
 * @param {Project} project
 * @returns {string | undefined}
 */
function getResponsibleUserName(project: Project): string | undefined {
  const responsibleMember = project.members?.find(member => member.isResponsible)

  return responsibleMember ? new ProjectMember(responsibleMember).user.fullName : undefined
}

/**
 * Sorts projects by optional text while keeping missing values last.
 * @param {Project} projectA The first project.
 * @param {Project} projectB The second project.
 * @param {(project: Project) => string | undefined} getText Gets the text to compare.
 * @param {number} direction The sorting direction multiplier.
 * @returns {number} The comparison result.
 */
function sortByOptionalProjectText(
  projectA: Project,
  projectB: Project,
  getText: (project: Project) => string | undefined,
  direction: number
): number {
  const textA = getText(projectA)
  const textB = getText(projectB)

  if (!textA && !textB) {
    return projectA.name.localeCompare(projectB.name) * direction
  }

  if (!textA) {
    return 1
  }

  if (!textB) {
    return -1
  }

  return (textA.localeCompare(textB) || projectA.name.localeCompare(projectB.name)) * direction
}
